import * as React from "react";
import { ImageOverlay, Map, Polygon, Polyline, Popup, Tooltip, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet.gridlayer.googlemutant";
import Wkt from "wicket/wicket-leaflet";
import APIClient from "../../cloudfarm/client";
import moment from "moment";

import cropr from "../../shared/utils/cropr";
import gettext from "../../shared/utils/gettext";
import { View, $, Model, _, js_settings } from "../utils/legacy";

class LeafletMap extends React.Component {
  state = {
    popUp: null,
    initialZoom: true,
  };
  static defaultProps = {
    locations: [],
    extraLayers: false,
    fields: null,
    selectedCropfields: [],
    imageZIndex: 1000,
  };

  componentDidMount() {
    cropr.defaultStyle = function (block) {
      var def = {
        fillColor: block.get("color")
          ? block.get("color")
          : block
          ? cropr.getCropColor(block.get("crop"))
          : "#993300",
        clickable: true,
        fillOpacity: 0,
      };
      if (block.isLine()) {
        return $.extend(def, {
          weight: block.get("hover") ? 5 : 3,
          opacity: 1.0,
          color: "#161099",
          dashArray: "5",
        });
      } else if (!(block.get("selected") || block.get("hover") || block.get("preview"))) {
        return $.extend(def, {
          weight: 1,
          opacity: 1.0,
          color: "#993300",
        });
      } else {
        return $.extend(def, {
          weight: block.get("hover") ? 5 : 3,
          color: block.get("selected") ? "#00b7ca" : "#993300",
          dashArray: "",
        });
      }
    };
    var that = this;
    const map = this.map;
    map.options = Object.assign(map.options, {
      featureClick: this.featureClick,
      featureHover: this.featureHover,
      zoomToFields: true,
      extraStyle: { clickable: true, fillOpacity: 0 },
    });
    this.addBaseLayers();
    if (this.props.extraLayers) {
      this.addExtraLayers();
    }

    function bindCropfields() {
      cropr.setCropFieldsVisibleByPlan();
      const updateCropfields = that.updateCropfields;
      cropr.cropfields.on("change:visible", (eventType) => {
        updateCropfields();
      });
      if (that.props.showFields && _) {
        cropr.addFields(that.map, cropr.cropfields.where({ visible: true }));
        const selectedCropfields = that.props.selectedCropfields;
        cropr.cropfields.each((cf) => {
          let isSelected = selectedCropfields.indexOf(cf.get("did")) > -1;
          cf.set("selected", isSelected);
          that.updateStyle(cf);
        });
        cropr.zoomToFields(that.map);
      }
      if (that.props.onCropfieldsReady) {
        that.props.onCropfieldsReady();
      }
    }

    if (cropr.farm) {
      if (cropr.features.cloudfarm) {
        bindCropfields();
      } else {
        this.cropfieldV3Collection = new Model.CropFieldV3Collection({
          farm_did: cropr.farm.get("did"),
          plan_did: cropr.getActivePlan().get("did"),
          application_feature: "cloudfarm",
          fields: "id",
        });
        this.cropfieldV3Collection.fetch({
          success: function (cropfieldV3Collection) {
            var cropfieldDids = cropfieldV3Collection.pluck("id");
            // NOTE: filtering on cropfields enabled by cloudfarm feature (implemented in V3 api)
            cropr.cropfields = new Model.CropFieldCollection(
              cropr.cropfields.filter(function (c) {
                return _.contains(cropfieldDids, c.get("did"));
              })
            );
            bindCropfields();
          },
        });
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.showFields) {
      const selectedCropfields = this.props.selectedCropfields;
      cropr.cropfields.each((cf) => {
        let isSelected = selectedCropfields.indexOf(cf.get("did")) > -1;
        cf.set("selected", isSelected);
        this.updateStyle(cf);
      });
    }
  }

  updateCropfields = () => {
    let map = this.map;
    if (!map || !map._layers) {
      return;
    }
    Object.entries(map._layers).forEach(([k, layer]) => {
      if (layer._point || layer.reference) {
        map.removeLayer(layer);
      }
    });
    cropr.addFields(map, cropr.cropfields.where({ visible: true }));
  };

  featureClick = (e) => {
    this.props.updateSelectedCropfields(e.target.reference);
  };

  featureHover = (e) => {
    const block = e.target.reference;
    block.set("hover", e.type === "mouseover");
    this.updateStyle(block);
  };

  updateStyle(block) {
    if (!(block.features && block.features[0])) return;
    block.features[0].setStyle(cropr.defaultStyle(block));
  }

  getBounds(fields) {
    if (fields && fields.length > 0) {
      const bounds = L.featureGroup(fields).getBounds();
      return { bounds: bounds };
    } else if (this.state.initialZoom) {
      this.setState({ initialZoom: false });
      if (cropr.farm && cropr.farm.get("address") && cropr.farm.get("address")["position"]) {
        const wkt = new Wkt.Wkt();
        const point = wkt.read(cropr.farm.get("address")["position"]).components;
        return { position: [point[0]["y"], point[0]["x"]] };
      } else if (cropr.getLeafletBounds()) {
        return { bounds: cropr.getLeafletBounds() };
      } else {
        return {};
      }
    } else {
      return {};
    }
  }

  onImageClick = (e) => {
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
    this.getPopUp(e);
  };

  onImageHover = (e) => {
    e.originalEvent.preventDefault();
    e.originalEvent.stopPropagation();
  };

  async getPopUp(e) {
    const selectedMeasurements = this.props.locations.filter(
      (l) => l.type === "image" && l.measurementLayer && !isNaN(l.measurementLayer.measurementId)
    );
    if (selectedMeasurements.length === 0) {
      return;
    }
    const measurementIds = selectedMeasurements.map(
      (l) => ("" + l.measurementLayer.measurementId).split("/")[0]
    );
    const payload = {
      x: e.latlng.lng,
      y: e.latlng.lat,
      ids: measurementIds.join(),
    };
    if (cropr.farmcontactdid) {
      payload.farmcontactdid = cropr.farmcontactdid;
    }
    let data = await APIClient.get(
      `/api/${cropr.cloudfarm_oauth_token ? "" : "v3/cloud_farm/"}data/datasets/values/`,
      payload,
      cropr.cloudfarm_oauth_token
    );

    const contentData = [];
    selectedMeasurements.forEach((l) => {
      const ml = l.measurementLayer;
      const pointData = data[("" + l.measurementLayer.measurementId).split("/")[0]];
      if (pointData) {
        const dateTime = pointData.time
          ? " (" + moment.utc(pointData.time).format("LLLL") + ")"
          : "";
        const name = cropr.isobus_measurement_types[ml.measurement.name]
          ? cropr.isobus_measurement_types[ml.measurement.name].name
          : ml.measurement.name;
        contentData.push({
          id: ml.measurementId,
          name: name,
          time: dateTime,
          value: pointData.value,
          unit: pointData.unit,
        });
      }
    });
    const content = contentData.map((cd, i) => (
      <div key={i}>
        <p style={{ lineHeight: "0px" }}>{cd.name}</p>
        <p style={{ lineHeight: "0px" }}>
          <b>
            {cropr.toLocaleFixed(cd.value, 2)} {cd.unit}
          </b>
          {cd.time}
        </p>
      </div>
    ));
    this.setState({
      popUp: { latLng: e.latlng, content: content.length > 0 ? content : gettext("No data") },
    });
  }

  render() {
    const fields = [],
      components = [];
    this.props.locations.forEach((location, i) => {
      let wkt, field;
      switch (location.type) {
        case "geometry":
          wkt = new Wkt.Wkt();
          wkt.read(location.wkt);
          field = wkt.toObject();
          if (location.line) {
            components.push(
              <Polyline
                color="#161099"
                dashArray="5"
                positions={field.getLatLngs()}
                key={i + location.toolTip}
              >
                {location.toolTip ? (
                  <Tooltip permanent>
                    <strong>{location.toolTip}</strong>
                  </Tooltip>
                ) : null}
              </Polyline>
            );
          } else {
            components.push(
              <Polygon positions={field.getLatLngs()} key={i + location.toolTip}>
                {location.toolTip ? <Tooltip permanent>{location.toolTip}</Tooltip> : null}
              </Polygon>
            );
          }
          if (location.zoomTo) {
            fields.push(field);
          }
          break;
        case "image":
          const boundingBox = location.bounds;
          if (!boundingBox) return console.error("LeafletMap; missing bounds for image");
          const imageBounds = [
              [boundingBox[1], boundingBox[0]],
              [boundingBox[3], boundingBox[2]],
            ],
            imageOverlay = L.imageOverlay(location.url, imageBounds);
          components.push(
            <ImageOverlay
              url={location.url}
              interactive={true}
              zIndex={this.props.imageZIndex}
              onMouseOver={this.onImageHover}
              onMouseOut={this.onImageHover}
              onClick={this.onImageClick}
              bounds={imageOverlay.getBounds()}
              key={location.key}
              opacity={location.opacity}
            />
          );
          fields.push(imageOverlay);
          break;
        case "model":
          wkt = new Wkt.Wkt();
          wkt.read(location.get("geometry"));
          field = wkt.toObject();
          components.push(<Polygon positions={field.getLatLngs()} key={location.id}></Polygon>);
          break;
        default:
          console.warn("Unknown location type: " + location.type);
      }
    });
    const boundsPosition = this.getBounds(fields);

    return (
      <Map
        style={{ height: this.props.height || "calc(100vh - 64px)" }}
        bounds={boundsPosition.bounds}
        center={boundsPosition.position}
        zoom={13}
        ref={(m) => {
          this.map = m && m.leafletElement;
        }}
        id="map"
        zoomControl={false}
      >
        {components}
        {this.state.popUp ? (
          <Popup onClose={() => this.setState({ popUp: null })} position={this.state.popUp.latLng}>
            {this.state.popUp.content}
          </Popup>
        ) : null}
        <ZoomControl position="topright" />
      </Map>
    );
  }

  addBaseLayers() {
    const map = this.map;
    // Only add Google and OpenStreetMap here - aerial photos are added in addExtraLayers()
    // with proper display names like "Luchtfoto 2025" instead of technical codes
    let bg = ["google", "openstreetmap"];
    let baseMaps = {};
    let baselayer = cropr.getItemFromLocalstorage("baselayer", bg);
    const protocol = window.location.protocol.replace("file", "https");
    const osmUrl = "//{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

    bg.forEach(function (layerName) {
      var layer;
      if (layerName === "google" && typeof google !== "undefined") {
        layer = L.gridLayer.googleMutant({
          type: "hybrid",
          styles: [],
        });
      } else if (layerName === "openstreetmap") {
        layer = new L.TileLayer(protocol + osmUrl, {
          attribution: "© OpenStreetMap contributors",
          opacity: 0.8,
        });
      } else {
        return console.warn("Can not add unknown layer type %o", layerName);
      }

      if (baselayer === layerName) map.addLayer(layer);
      baseMaps[layerName.toLowerCase()] = layer;
    });

    map.layersSwitch = L.control.layers(baseMaps, {}, { position: "topright", collapsed: true });
    map.layersSwitch.addTo(map);
    map.on("baselayerchange", function (e) {
      localStorage.setItem("baselayer", e.name);
    });
    map.on("overlayadd", function (e) {
      localStorage.setItem(e.name + "overlay", "on");
    });
    map.on("overlayremove", function (e) {
      localStorage.setItem(e.name + "overlay", "off");
    });

    if (cropr.max_bounding_box) {
      var southWest = [cropr.max_bounding_box.bottom, cropr.max_bounding_box.left],
        northEast = [cropr.max_bounding_box.top, cropr.max_bounding_box.right];
      map.setMaxBounds([southWest, northEast]);
    }
    if (!this.props.extraLayers) {
      return;
    }

    if (cropr.session && !this.mapReferenceDatePickerView && _) {
      this.mapReferenceDatePickerView = View.MapReferenceDateView
        ? new View.MapReferenceDateView({ leaflet: true, el: $("#map") })
        : null;
    }
  }

  addExtraLayers() {
    const map = this.map;
    var baseLayers = {},
      legends = {},
      dataSources = [],
      wmsSources = [],
      countries = cropr.cropfields.pluck("country"),
      isInNl = countries.indexOf("NL") > -1,
      isInBe = countries.indexOf("BE") > -1,
      addCountrySuffix = isInNl && isInBe;
    if (isInNl) {
      var attributionPdok = 'Open data via <a href="https://www.pdok.nl/">pdok.nl</a>';
      var suffixNl = addCountrySuffix ? " (NL)" : "";
      dataSources.push({ name: "Grondsoorten" + suffixNl, src: 25, legend: "NLS06" });
      dataSources.push({ name: "Grondwater bescherming" + suffixNl, src: 35, legend: "NLGWP" });
      dataSources.push({
        name: "Watertrappen" + suffixNl,
        src: 33,
        legend: "NLGWL",
        updateLegend: "water_trap",
        lazy_legend: true,
        country: "NL",
      });
      // zie https://reference.crop-r.com/admin/reference/source/?q=BRP
      Object.entries({
        1229: "25",
        1227: "24",
        183: "23",
        158: "22",
        147: "21",
        114: "20",
        98: "19",
        62: "18",
        48: "17",
        29: "16",
        27: "15",
        15: "14",
        14: "13",
        16: "12",
        13: "11",
        12: "10",
        11: "09",
      }).forEach(([src, y]) => {
        dataSources.push({
          name: "Gewassen 20" + y + suffixNl,
          src: src,
          legend: "BRP" + y,
          type: "geo",
          lazy_legend: true,
          year: "20" + y,
          country: "NL",
        });
      });
      wmsSources.push({
        name: "Kadaster" + suffixNl,
        url: "https://service.pdok.nl/kadaster/kadastralekaart/wms/v5_0",
        options: {
          format: "image/png",
          transparent: "true",
          attribution: attributionPdok,
          opacity: 1,
          minZoom: 16,
          minNativeZoom: 17,
          layers: "kadastralekaart",
          style: "default",
        },
        legend: [
          { color: "#000000", name: "Kadastrale percelen" },
          { color: "#EB3727", name: "Gebouwen" },
        ],
      });
      wmsSources.push({
        name: "Hoogte 2021" + suffixNl,
        url: "https://service.pdok.nl/rws/actueel-hoogtebestand-nederland/wms/v1_0",
        options: {
          format: "image/png",
          transparent: "true",
          style: "default",
          attribution: attributionPdok,
          opacity: 0.5,
          layers: "dsm_05m",
        },
        legend: [
          { color: "#53739B", name: "-5 meter" },
          { color: "#6FB2A4", name: "0 meter" },
          { color: "#86E35D", name: "5 meter" },
          { color: "#D0F562", name: "10 meter" },
          { color: "#F7FB65", name: "15 meter" },
          { color: "#EDC77D", name: "50 meter" },
          { color: "#C58070", name: "250 meter" },
        ],
      });
      wmsSources.push({
        name: "Hoogte 2025" + suffixNl,
        urls: [
          {
            url: "https://api.ellipsis-drive.com/v3/ogc/wms/a4a8a27b-e36e-4dd5-a75b-f7b6c18d33ec",
            layers: "fc9d369f-94ca-4373-8281-a6854edb67c9_a3ba9a7a-c7c7-4fee-b5e7-09dd8ac265e7",
          },
          {
            url: "https://api.ellipsis-drive.com/v3/ogc/wms/22f5740b-1276-42e0-9d2d-dd2a9745e928",
            layers: "c936131a-2c4f-44ce-b321-a085c6c0ce3b_4202a0e1-781d-4ce9-b2d7-ff9a99fe795c",
          },
        ],
        options: {
          format: "image/png",
          transparent: true,
          version: "1.3.0",
          opacity: 0.5,
        },
        legend: [
          { color: "#53739B", name: "-5 meter" },
          { color: "#6FB2A4", name: "0 meter" },
          { color: "#86E35D", name: "5 meter" },
          { color: "#D0F562", name: "10 meter" },
          { color: "#F7FB65", name: "15 meter" },
          { color: "#EDC77D", name: "50 meter" },
          { color: "#C58070", name: "250 meter" },
        ],
      });

      baseLayers["BRT Achtergrond" + suffixNl] = L.tileLayer(
        "https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0?REQUEST=GetTile&SERVICE=WMTS&VERSION=1.0.0&TILEMATRIXSET=EPSG:3857&LAYER=standaard&FORMAT={format}&TILECOL={x}&TILEROW={y}&TILEMATRIX={z}",
        {
          format: "image/png",
          transparent: "false",
          style: "default",
          attribution: attributionPdok,
          opacity: 1,
          layers: "standaard",
        }
      );
      // Add "Actueel" (current) layer
      baseLayers["Luchtfoto Actueel" + suffixNl] = L.tileLayer(
        "https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/{layer}/EPSG:3857/{z}/{x}/{y}.jpeg",
        {
          format: "image/jpeg",
          transparent: "false",
          style: "default",
          attribution: attributionPdok,
          opacity: 1,
          layer: "Actueel_orthoHR",
        }
      );
    }
    if (isInBe) {
      var suffixBe = addCountrySuffix ? " (BE)" : "";
      var attributionGeopunt = 'Open data via <a href="https://geopunt.be/">geopunt.be</a>';
      dataSources.push({ name: "Grondsoorten" + suffixBe, src: 63, legend: "BES17" });
      dataSources.push({ name: "Grondwater bescherming" + suffixBe, src: 53, legend: "BEGWP" });
      dataSources.push({
        name: "Kadaster" + suffixBe,
        src: 1234,
        legend: "BEKDS",
        type: "cadastre",
        backupLegend: [{ color: "#85BFD0", name: "Kadastrale percelen" }],
      });
      // zie https://reference.crop-r.com/admin/reference/source/?q=EPR
      Object.entries({
        1228: "25",
        1226: "24",
        177: "23",
        159: "22",
        146: "21",
        115: "20",
        102: "19",
        72: "18",
        49: "17",
        40: "16",
        28: "15",
        10: "14",
        9: "13",
        8: "12",
        7: "11",
        6: "10",
        5: "09",
        4: "08",
      }).forEach(([src, y]) => {
        dataSources.push({
          name: "Gewassen 20" + y + suffixBe,
          src: src,
          legend: "EPR" + y,
          type: "geo",
          lazy_legend: true,
          year: "20" + y,
          country: "BE",
        });
      });
      wmsSources.push({
        name: "Hoogte" + suffixBe,
        url: "https://geo.api.vlaanderen.be/DHMV/wms",
        options: {
          format: "image/png",
          transparent: "true",
          attribution: attributionGeopunt,
          opacity: 0.5,
          layers: "DHMVII_DTM_1m",
        },
        legend: [
          { color: "#53739B", name: "0 meter" },
          { color: "#6FB2A4", name: "50 meter" },
          { color: "#86E35D", name: "100 meter" },
          { color: "#D0F562", name: "150 meter" },
          { color: "#F7FB65", name: "200 meter" },
          { color: "#EABB61", name: "250 meter" },
          { color: "#C58070", name: "300 meter" },
        ],
      });
      var currentYearBe = new Date().getFullYear() - 2;
      baseLayers["Luchtfoto " + currentYearBe + suffixBe] = L.tileLayer.wms(
        "https://geo.api.vlaanderen.be/omw/wms",
        {
          format: "image/png",
          transparent: "false",
          attribution: attributionGeopunt,
          opacity: 1,
          layers: "OMWRGB" + String(currentYearBe).substring(2) + "VL",
        }
      );
    }
    // dataSources.push({name: 'Soil texture USDA', src: 97, legend: 'LUCAS', type: 'raster'})
    Object.entries(baseLayers).forEach(([name, baseLayer]) => {
      map.layersSwitch.addBaseLayer(baseLayer, name);
    });

    dataSources.forEach(function (spec) {
      spec["layer"] = L.tileLayer(
        `${js_settings.REFERENCE_TILE_DOMAIN}/{type}reference/tile/{z}/{x}/{y}.png?src={src}`,
        {
          tms: true,
          maxZoom: 21,
          type: spec.type || "data",
          src: spec.src,
          opacity: 0.5,
        }
      );
      var legendControl = L.control({ position: "topright" });
      if (spec.lazy_legend) {
        let updateLegend = () => {
          $.ajax({
            type: "GET",
            url: `${js_settings.REFERENCE_DOMAIN}/georeference_s/`,
            data: {
              bbox: map.getBounds().toBBoxString(),
              legend: spec.updateLegend || "parcel_" + spec.year,
              country: spec.country,
            },
            success: function (data) {
              $("div.legend_" + spec.legend).hide();
              data.forEach(function (val) {
                $("div.legend_" + spec.legend + "." + val).show();
              });
            },
            headers: js_settings.JWT_REFERENCE,
            dataType: "json",
          });
        };
        legendControl.onAdd = function (map) {
          var div = L.DomUtil.create("div", "overlay-legend");
          $.ajax({
            type: "GET",
            url: `${js_settings.REFERENCE_DOMAIN}/codes/` + spec.legend + "/groups",
            success: function (data) {
              div.innerHTML = Object.entries(data)
                .map(([key, val]) => {
                  const keyClass = key.split(",").join(" ");
                  return `<div class="legend_${spec.legend} ${keyClass}" style="display: none"><i style="background:${val.color}"></i> ${val.name}<br></div>`;
                })
                .join("");
              updateLegend();
            },
            headers: js_settings.JWT_REFERENCE,
            dataType: "json",
          });
          map.on("moveend", updateLegend);
          return div;
        };
        legendControl.onRemove = function (map) {
          map.off("moveend", updateLegend);
        };
      } else {
        legendControl.onAdd = function (map) {
          var div = L.DomUtil.create("div", "overlay-legend");
          $.ajax({
            type: "GET",
            url: `${js_settings.REFERENCE_DOMAIN}/codes/` + spec.legend + "/groups",
            success: function (data) {
              _.each(_.isEmpty(data) ? spec.backupLegend : data, function (val) {
                div.innerHTML +=
                  '<i style="background:' + val.color + '"></i> ' + val.name + "<br>";
              });
            },
            headers: js_settings.JWT_REFERENCE,
            dataType: "json",
          });
          return div;
        };
      }
      legends[spec.name] = legendControl;
    });
    wmsSources.forEach(function (spec) {
      if (spec.urls) {
        var layers = spec.urls.map(function (urlSpec) {
          return L.tileLayer.wms(urlSpec.url, { ...spec.options, layers: urlSpec.layers });
        });
        spec["layer"] = L.layerGroup(layers);
      } else {
        spec["layer"] = L.tileLayer.wms(spec.url, spec.options);
      }
      var legendControl = L.control({ position: "topright" });
      legendControl.onAdd = function (map) {
        var div = L.DomUtil.create("div", "overlay-legend");
        spec.legend.forEach(function (val) {
          div.innerHTML += '<i style="background:' + val.color + '"></i> ' + val.name + "<br>";
        });
        return div;
      };
      legends[spec.name] = legendControl;
    });
    if (_) {
      _.each(
        _.sortBy(dataSources.concat(wmsSources), function (spec) {
          return addCountrySuffix
            ? spec.name.substring(spec.name.length - 3) + spec.name
            : spec.name;
        }).reverse(),
        function (spec) {
          map.layersSwitch.addOverlay(spec.layer, spec.name);
        }
      );
    }
    map.on("overlayadd", function (e) {
      if (legends[e.name]) {
        legends[e.name].addTo(map);
      }
    });
    map.on("overlayremove", function (e) {
      if (legends[e.name]) {
        map.removeControl(legends[e.name]);
      }
    });
  }
}

export default LeafletMap;
